import React, { useState } from "react";
import axios from "axios";
import apiConfig from "../../config/api.js";

/**
 * Composant pour la réinitialisation du mot de passe
 * Permet à l'utilisateur de définir un nouveau mot de passe avec un token
 */
const ResetPasswordForm = ({ resetToken, onBack, onClose }) => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Gérer le changement des champs
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Gérer la soumission du formulaire
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation côté client
    if (formData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${apiConfig.baseURL}/users/reset-password`,
        {
          token: resetToken,
          password: formData.password,
        },
      );

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (error) {
      console.error("Erreur lors de la réinitialisation:", error);
      setError(
        error.response?.data?.message ||
          "Erreur lors de la réinitialisation du mot de passe",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full animate-slide-in-up">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-6 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">🔐 Nouveau mot de passe</h2>
              <p className="text-green-100 text-sm mt-1">
                Définissez un nouveau mot de passe pour votre compte
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-all duration-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Message de succès */}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl">
              <div className="flex items-center">
                <span className="text-green-500 mr-2">✅</span>
                <div>
                  <p className="font-semibold">Mot de passe réinitialisé !</p>
                  <p className="text-sm mt-1">
                    Votre mot de passe a été modifié avec succès. Vous pouvez
                    maintenant vous connecter.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center">
                <span className="text-red-500 mr-2">❌</span>
                {error}
              </div>
            </div>
          )}

          {!success && (
            <>
              {/* Nouveau mot de passe */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  🔒 Nouveau mot de passe
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-base"
                  placeholder="Au moins 6 caractères"
                  required
                  minLength={6}
                />
              </div>

              {/* Confirmation du mot de passe */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  🔒 Confirmer le mot de passe
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-base"
                  placeholder="Répétez le mot de passe"
                  required
                  minLength={6}
                />
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Réinitialisation...
                    </div>
                  ) : (
                    "✅ Réinitialiser"
                  )}
                </button>

                <button
                  type="button"
                  onClick={onBack}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300 transform hover:scale-105"
                >
                  Annuler
                </button>
              </div>
            </>
          )}

          {success && (
            <div className="mt-4">
              <button
                type="button"
                onClick={onBack}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Retour à la connexion
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
